import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import { useInfiniteScrollEvent } from '@/hooks/useInfiniteScrollEvent';
import { cn } from '@/utils/tailwind.util';
import { INITIAL_QUIZ_BANK_FILTER_STATE, QUIZ_BANK_LOAD_MORE_SKELETON_COUNT } from '../consts';
import { useQuizBankSelection, useQuizBankTracking } from '../hooks';
import { type QuizBankPlace } from '../tracking/events.quiz-bank';
import { type QuizBankFallbackState, type QuizBankFilterOption, type QuizBankFilterState, type QuizBankProblem, type QuizBankQuizType, type QuizBankSortKey } from '../types';
import { QuizBankFilterPanel } from './QuizBankFilterPanel';
import { QuizBankProblemCardSkeleton } from './QuizBankProblemCardSkeleton';
import { QuizBankProblemList } from './QuizBankProblemList';
import { QuizBankResultToolbar } from './QuizBankResultToolbar';
import { QuizBankSelectionBar } from './QuizBankSelectionBar';
export interface QuizBankViewProps {
  /**
   * 이 화면이 열린 표면. 신규 생성 모달과 편집 화면 재진입이 같은 뷰를 쓰므로,
   * 두 퍼널을 가르려면 열어 준 쪽이 자기 자리를 말해야 한다(트래킹 공통 문맥).
   */
  place: QuizBankPlace;
  /**
   * 7자리 카테고리 트리 id — `quiz_bank_modal_open` 의 join key 라 이탈(`..._close`)도 같은 키를
   * 싣는다. 조회는 컨테이너 몫이라 뷰는 트래킹에만 쓴다. 스토리·직접 렌더는 비워 둘 수 있다.
   */
  categoryTreeId?: string;
  /** 현재 페이지까지 누적된 문항 */
  problems: QuizBankProblem[];
  /** 키워드가 단원명에 걸린 단원 → hit 자리(서버 첫 페이지 `matchedUnits`). 카드가 단원명을 칠한다 */
  unitHitTermsById?: ReadonlyMap<string, readonly string[]>;
  /** 코드 검색이 걸린 성취기준 코드(서버 첫 페이지 `matchedStandards`). 카드가 그 배지를 칠한다 */
  matchedStandardCodes?: ReadonlySet<string>;
  gradeOptions: QuizBankFilterOption[];
  unitOptions: QuizBankFilterOption[];
  typeOptions: QuizBankFilterOption[];
  /** 서버가 첫 페이지에만 내려주므로 없을 수 있다 */
  totalCount?: number;

  /** 필터는 controlled — 서버가 필터링을 소유하므로 바뀌면 밖에서 다시 조회한다 */
  filter: QuizBankFilterState;
  onFilterChange: (next: QuizBankFilterState) => void;
  /** 0건 자동 완화 상태(`useQuizBankSearchFallback`) */
  fallback?: QuizBankFallbackState;
  status: 'loading' | 'success' | 'error';
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  /**
   * 다음 페이지 조회만 실패했다. 이미 그린 목록은 그대로 두고 하단에서만 재시도를 받는다 —
   * 첫 조회 실패(`status: 'error'`)와 달리 선택 맥락을 잃게 할 이유가 없다.
   */
  isNextPageError?: boolean;
  fetchNextPage?: () => Promise<unknown>;

  /** 이미 빌드에 담긴 문제 ID — 중복 담기를 막는다 */
  addedProblemIds?: string[];
  /** 더 담을 수 있는 개수 */
  maxSelectableCount?: number;
  onAdd: (problems: QuizBankProblem[]) => void;
  onRetry?: () => void;
}
const EMPTY_IDS: string[] = [];

/** 모듈 상수라 스크롤 리스너가 매 렌더 다시 붙지 않는다. */
const NOOP_FETCH_NEXT_PAGE = async () => {};
const toggleIn = <T,>(values: T[], value: T): T[] => values.includes(value) ? values.filter(item => item !== value) : [...values, value];

/**
 * 문제 은행 본문 + 하단 담기 바. 모달 셸과 네트워크는 모른다 —
 * 조회는 `QuizBankViewContainer`, 셸은 `QuizSetupModal`/`quizBankModal` 이 갖는다.
 */
export const QuizBankView = ({
  place,
  categoryTreeId = '',
  problems,
  unitHitTermsById,
  matchedStandardCodes,
  gradeOptions,
  unitOptions,
  typeOptions,
  totalCount,
  filter,
  onFilterChange,
  fallback,
  status,
  hasNextPage = false,
  isFetchingNextPage = false,
  isNextPageError = false,
  fetchNextPage = NOOP_FETCH_NEXT_PAGE,
  addedProblemIds = EMPTY_IDS,
  maxSelectableCount,
  onAdd,
  onRetry
}: QuizBankViewProps) => {
  const {
    t
  } = useTranslation(['build']);
  // 검색어는 초안과 적용값을 분리한다 — 타이핑마다 서버를 때리지 않는다.
  const [searchDraft, setSearchDraft] = useState(filter.query);
  // 모바일 전용 — 필터와 결과를 한 번에 하나만 보여준다. 태블릿 이상은 나란히 있다.
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const {
    selectedProblems,
    selectedIds,
    isFull,
    toggle
  } = useQuizBankSelection({
    addedProblemIds,
    maxSelectableCount
  });
  const patch = (next: Partial<QuizBankFilterState>) => onFilterChange({
    ...filter,
    ...next
  });

  /** 학년은 언제나 하나가 잡혀 있다(컨테이너가 시드한다). 아직 못 잡았으면 빈 문자열. */
  const gradeId = filter.gradeId ?? '';
  const resultCount = totalCount ?? problems.length;

  /**
   * 0건·실패 노출을 **적용된 필터 조합마다** 다시 세기 위한 키.
   * 키가 같으면 같은 막다른 길이므로 노출 1회로 본다 — 스크롤로 들락거릴 때마다
   * 세면 같은 상태가 여러 건이 된다.
   */
  const resultStateKey = `${gradeId}|${filter.unitIds.join(',')}|${filter.types.join(',')}|${filter.query}`;

  // 분석 이벤트는 전부 이 훅이 소유한다 — 아래 핸들러는 발화 순서만 정한다.
  const tracking = useQuizBankTracking({
    place,
    categoryTreeId,
    filter,
    status,
    resultStateKey,
    resultCount,
    loadedProblemCount: problems.length,
    unitHitTermsById,
    matchedStandardCodes,
    selectedProblems,
    addedProblemCount: addedProblemIds.length,
    maxSelectableCount,
    fallback
  });

  /*
    스크롤 핸들러는 **아이덴티티가 고정**돼야 한다. `useInfiniteScrollEvent` 는 ref 콜백이라,
    넘긴 함수가 매 렌더 새로 만들어지면 React 가 리스너를 떼었다 다시 붙이며 훅 안의 중복 호출
    가드와 throttle 까지 초기화한다 — 그러면 요청 중에도 다음 스크롤이 같은 페이지를 다시 부른다.
  */
  const fetchNextPageRef = useRef(fetchNextPage);
  fetchNextPageRef.current = fetchNextPage;
  const {
    nextPageStarted
  } = tracking;
  const handleFetchNextPage = useCallback(() => {
    nextPageStarted();
    return fetchNextPageRef.current();
  }, [nextPageStarted]);
  const handleSelectGrade = (nextGradeId: string) => {
    tracking.gradeChanged(nextGradeId);
    // 학년이 바뀌면 이전 학년의 단원 선택은 남겨 둘 수 없다 —
    // 그 단원들은 새 학년 목록에 아예 없어서 결과가 0건이 된다.
    patch({
      gradeId: nextGradeId,
      unitIds: []
    });
  };
  const handleToggleUnit = (unitId: string) => {
    const nextUnitIds = toggleIn(filter.unitIds, unitId);
    tracking.filterToggled({
      axis: 'unit',
      value: unitId,
      checked: nextUnitIds.includes(unitId),
      unitFilterCount: nextUnitIds.length,
      typeFilterCount: filter.types.length
    });
    patch({
      unitIds: nextUnitIds
    });
  };
  const handleToggleType = (type: string) => {
    const nextTypes = toggleIn(filter.types, type as QuizBankQuizType);
    tracking.filterToggled({
      axis: 'type',
      value: type,
      checked: nextTypes.includes(type as QuizBankQuizType),
      unitFilterCount: filter.unitIds.length,
      typeFilterCount: nextTypes.length
    });
    patch({
      types: nextTypes
    });
  };
  const handleToggleProblem = (problem: QuizBankProblem) => {
    /*
      가드를 여기서 다시 걸지 않는다 — 잠긴 카드(이미 담긴 문항 · 상한 도달)는
      `QuizBankProblemCard.handleToggle` 이 `locked` 로 먼저 끊어서 이 핸들러까지
      오지 않는다. 여기서 한 번 더 끊으면 발화가 조건부가 되어 계측 감사가
      "이 경로가 정말 찍히는가"를 증명하지 못한다.
    */
    const willSelect = !selectedIds.includes(problem.id);
    toggle(problem);
    tracking.problemToggled(problem, willSelect);
  };
  const handleImport = () => {
    tracking.importSubmitted();
    onAdd(selectedProblems);
  };

  /**
   * 다음 페이지를 부를 수 있는가. 요청 중(`isFetchingNextPage`)에는 닫아 둔다 —
   * `fetchNextPage` 는 기본값 `cancelRefetch: true` 라 재호출이 진행 중 요청을 끊는다.
   * 실패한 뒤에도 닫는다 — 열어 두면 같은 요청을 무한히 되쏜다(하단 재시도로 받는다).
   * 첫 조회 중에는 `hasNextPage` 가 이전 필터(placeholder) 값이라 막는다.
   */
  const canLoadMore = status === 'success' && hasNextPage && !isFetchingNextPage && !isNextPageError;

  /**
   * 스크롤 지표를 이벤트 시점에 읽어 다음 페이지를 부른다 (`AllResponsesModal` 과 동일).
   * IntersectionObserver 는 다음 프레임에 보고해 응답 직후 렌더와 시점이 어긋난다.
   */
  const attachScroll = useInfiniteScrollEvent<HTMLDivElement>({
    hasNextPage: canLoadMore,
    fetchNextPage: handleFetchNextPage
  });

  /*
    스크롤이 **날 수 없는** 상태에서도 목록을 채운다. 응답 20건 중 상당수가 변환 단계
    (`toQuizBankProblems`)에서 빠지면 카드가 스크롤 영역보다 짧게 남고, 그러면 scroll
    이벤트가 영영 나지 않아 뒤 페이지의 멀쩡한 문항에 닿을 방법이 없다.
  */
  const scrollContainer = useRef<HTMLDivElement | null>(null);
  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    scrollContainer.current = node;
    attachScroll(node);
  }, [attachScroll]);
  useEffect(() => {
    if (!canLoadMore) return;
    const container = scrollContainer.current;
    // clientHeight 0 은 레이아웃 전(또는 숨김)이라 "넘치지 않는다"고 볼 수 없다.
    if (!container || container.clientHeight === 0) return;
    if (container.scrollHeight > container.clientHeight) return;
    void handleFetchNextPage();
  }, [canLoadMore, problems.length, handleFetchNextPage]);

  // 학년은 교사가 잡아 둔 맥락이라 초기화해도 남긴다.
  const resetFilter = () => {
    tracking.filterReset();
    setSearchDraft('');
    onFilterChange({
      ...INITIAL_QUIZ_BANK_FILTER_STATE,
      gradeId: filter.gradeId,
      sort: filter.sort
    });
  };
  return <div className="flex h-full min-h-0 flex-col" data-sentry-component="QuizBankView" data-sentry-source-file="QuizBankView.tsx">
      <div className="flex min-h-0 flex-1">
        <QuizBankFilterPanel className={cn(isFilterOpen ? 'flex' : 'hidden', 'tablet-sm:flex')} onDone={() => setIsFilterOpen(false)} resultCount={totalCount ?? problems.length} gradeOptions={gradeOptions} unitOptions={unitOptions} typeOptions={typeOptions} unitFilterApplied={filter.unitIds.length > 0} onReset={resetFilter} onSelectGrade={handleSelectGrade} onToggleUnit={handleToggleUnit} onToggleType={handleToggleType} data-sentry-element="QuizBankFilterPanel" data-sentry-source-file="QuizBankView.tsx" />

        <div className={cn(isFilterOpen ? 'hidden' : 'flex', 'min-h-0 flex-1 flex-col gap-sm bg-neutral px-md pt-md tablet-sm:flex tablet-sm:px-lg')}>
          <QuizBankResultToolbar searchDraft={searchDraft} resultCount={totalCount ?? problems.length} sort={filter.sort} onSearchDraftChange={setSearchDraft} onSearchSubmit={() => {
          tracking.searchSubmitted(searchDraft, 'keyword_input');
          patch({
            query: searchDraft
          });
        }} onSearchClear={() => {
          tracking.searchCleared();
          setSearchDraft('');
          patch({
            query: ''
          });
        }} onSortChange={(sort: QuizBankSortKey) => {
          tracking.sortChanged(sort);
          patch({
            sort
          });
        }} onOpenFilter={() => setIsFilterOpen(true)} data-sentry-element="QuizBankResultToolbar" data-sentry-source-file="QuizBankView.tsx" />

          {/* 하단 여백은 스크롤 영역 안에 둬야 마지막 카드가 잘리지 않는다 */}
          <div ref={scrollRef} data-testid="quiz-bank-scroll-container" className="-mx-3xs min-h-0 flex-1 overflow-y-auto px-3xs pb-md">
            <QuizBankProblemList status={status} problems={problems} selectedIds={selectedIds} addedIds={addedProblemIds} selectionFull={isFull} query={filter.query} unitHitTermsById={unitHitTermsById} matchedStandardCodes={matchedStandardCodes}
          // 0건·실패 노출은 적용된 필터 조합마다 한 번씩 센다.
          resultStateKey={resultStateKey} onEmptyImpression={tracking.emptyResultViewed} onErrorImpression={tracking.errorViewed} onToggle={handleToggleProblem}
          // 단원명을 검색어로 건다. 왼쪽 단원 필터에 넣지 않는다 — 검색어라야 본문·단원명 hit 가 같이 칠해진다.
          onUnitClick={unitName => {
            setSearchDraft(unitName);
            tracking.searchSubmitted(unitName, 'unit_name');
            patch({
              query: unitName
            });
          }}
          // 성취기준 코드를 검색어로 건다. 서버가 코드로 조회하므로 단원 점프보다 정확하다.
          onStandardClick={standard => {
            setSearchDraft(standard.code);
            tracking.searchSubmitted(standard.code, 'standard_code');
            patch({
              query: standard.code
            });
          }}
          /* 상위가 `onRetry` 를 주지 않으면 버튼 자체가 없어서 발화도 그 조건 안에 둔다. */ onRetry={onRetry && (() => {
            tracking.retryClicked();
            onRetry();
          })} data-sentry-element="QuizBankProblemList" data-sentry-source-file="QuizBankView.tsx" />

            {isFetchingNextPage && <div aria-live="polite" aria-busy className="flex flex-col gap-sm pt-sm">
                <span className="sr-only">
                  {t('build:quizBank.list.loadingMore')}
                </span>
                {Array.from({
              length: QUIZ_BANK_LOAD_MORE_SKELETON_COUNT
            }).map((_, index) => <QuizBankProblemCardSkeleton key={index} />)}
              </div>}

            {/*
              다음 페이지만 실패했을 때. 목록·선택은 그대로 두고 여기서만 다시 받는다.
              `quiz_bank_result_retry_click` 은 전체 실패 화면(분모 `..._error_view`)의
              지표라 여기서 같이 쏘지 않는다 — 쏘면 두 표면이 한 비율에 섞인다.
             */}
            {isNextPageError && <div className="flex justify-center pt-sm">
                <Button variant="outlined" intent="neutral" size="md" onClick={() => void handleFetchNextPage()}>
                  {t('build:quizBank.error.retry')}
                </Button>
              </div>}
          </div>
        </div>
      </div>

      {/* 모바일 필터 화면에서는 푸터가 둘로 쌓이지 않게 담기 바를 감춘다 */}
      <QuizBankSelectionBar className={cn(isFilterOpen && 'hidden tablet-sm:flex')} selectedCount={selectedProblems.length} onAdd={handleImport} data-sentry-element="QuizBankSelectionBar" data-sentry-source-file="QuizBankView.tsx" />
    </div>;
};